import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, User, Store, ShieldCheck } from "lucide-react";
import { API } from "../lib/api";
import CarCard from "../components/CarCard";
import DealerCard from "../components/DealerCard";
import BannerSlider from "../components/BannerSlider";
import HeroSlider from "../components/HeroSlider";
import HeroSearch, { scrollToListings } from "../components/HeroSearch";
import StatesSlider from "../components/StatesSlider";
import { useUserLocation, locParams, locLabel } from "../lib/location";
import NearbyCars from "../components/NearbyCars";

const PRIVATE_IMG = "https://images.unsplash.com/photo-1663852408695-f57f4d75a536?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200";
const DEALER_IMG = "https://images.unsplash.com/photo-1783740486439-b4487fad649f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200";
const WRAP = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";

function CategoryCard({ to, img, icon: Icon, eyebrow, title, text, testid }) {
  return (
    <Link to={to} data-testid={testid} className="keep-dark group relative overflow-hidden rounded-xl border gold-line min-h-[84px] flex items-center">
      <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
      <div className="relative px-3 py-2.5 flex items-center gap-3 w-full">
        <span className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center" style={{ background: "var(--gold-grad)" }}><Icon className="w-3.5 h-3.5 text-black" /></span>
        <div className="min-w-0 flex-1">
          <p className="eyebrow !text-[8px]">{eyebrow}</p>
          <h3 className="font-display text-lg text-white leading-tight">{title}</h3>
          <p className="text-[11px] text-gray-300 line-clamp-1">{text}</p>
        </div>
        <p className="text-[#E5C158] text-xs hidden sm:flex items-center gap-1 shrink-0">View <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" /></p>
      </div>
    </Link>
  );
}

const SectionHead = ({ eyebrow, title, to, label, testid }) => (
  <div className="flex items-end justify-between mb-4 gap-4">
    <div><p className="eyebrow !text-[10px] mb-1">{eyebrow}</p><h2 className="font-display text-2xl sm:text-3xl">{title}</h2></div>
    {to && <Link to={to} className="btn-outline !py-1.5 !px-3 text-xs" data-testid={testid}>{label} <ArrowRight className="w-3.5 h-3.5" /></Link>}
  </div>
);

export default function Home() {
  const [latest, setLatest] = useState([]);
  const [dealers, setDealers] = useState([]);
  const { location } = useUserLocation();
  const lp = locParams(location);
  const key = JSON.stringify(lp);
  const city = location?.city;
  const place = location?.state ? locLabel(location) : "";
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setLoaded(false);
    axios.get(`${API}/listings`, { params: { limit: 8, ...lp } }).then((r) => { setLatest(r.data.items); setLoaded(true); });
    axios.get(`${API}/dealers`, { params: lp }).then((r) => setDealers(r.data.slice(0, 4)));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="home-page">
      <HeroSlider top={<BannerSlider className="mb-6 rise" />}>
        <HeroSearch />
      </HeroSlider>

      <StatesSlider onPick={() => setTimeout(scrollToListings, 150)} />

      <section className={`${WRAP} py-6`}>
        <div className="grid md:grid-cols-2 gap-3">
          <CategoryCard to="/cars?seller_type=private" img={PRIVATE_IMG} icon={User} eyebrow="Category 01" title="Private Owners" text="Buy directly from individual owners. No middlemen." testid="category-private" />
          <CategoryCard to="/dealers" img={DEALER_IMG} icon={Store} eyebrow="Category 02" title="Pre-Owned Dealers" text="Showroom inventories with store profiles & verified badges." testid="category-dealer" />
        </div>
      </section>

      <section id="home-listings" className={`${WRAP} py-6 scroll-mt-28`}>
        <SectionHead eyebrow={place ? `Only in ${place}` : "Featured & fresh"} title={place ? `Latest cars in ${place}` : "Latest listings"} to="/cars" label="View all" testid="view-all-cars" />
        {latest.length ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="home-latest-grid">{latest.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}</div>
        ) : (
          <div className="lux-card p-8 text-center text-gray-400 text-sm" data-testid="home-latest-empty">
            {place ? <>No cars listed in {place} yet. <Link to="/sell" className="text-[#E5C158]">List yours free</Link> or change your location from the top bar.</> : "No cars listed yet."}
          </div>
        )}
      </section>

      {city && loaded && <NearbyCars state={location.state} city={city} cityCount={latest.length} className={`${WRAP} py-6`} />}

      {dealers.length > 0 && (
        <section className={`${WRAP} py-6`}>
          <SectionHead eyebrow="Showrooms" title={place ? `Dealer stores in ${place}` : "Featured dealer stores"} to="/dealers" label="All dealers" testid="view-all-dealers" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{dealers.map((d) => <DealerCard key={d.id} dealer={d} />)}</div>
        </section>
      )}

      <section className={`${WRAP} pt-6`}>
        <div className="relative overflow-hidden rounded-2xl border gold-line p-5 sm:p-6 grain bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.18),transparent_60%)] bg-[#0d0f14]">
          <div className="relative z-10 flex flex-wrap gap-4 items-center justify-between">
            <div>
              <p className="eyebrow !text-[10px] flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Free to list</p>
              <h2 className="font-display text-2xl sm:text-3xl">Sell your car to serious buyers.</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to="/sell" className="btn-gold text-sm" data-testid="cta-sell-private">Sell as Owner</Link>
              <Link to="/dashboard?tab=store" className="btn-outline text-sm" data-testid="cta-open-store">Open Dealer Store</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
